<script lang="ts" setup>
	import type { TableColumnProps } from 'ant-design-vue'
	import { width } from 'happy-dom/lib/PropertySymbol'
	import useEmployeesApi, { type EmployeesPolicyStatus } from '~/services/apis/employeesApi'

	const props = defineProps<{
		loading?: boolean
		loadingTable?: boolean
		filter?: object
		status: EmployeesPolicyStatus | string
		employeeId: string
	}>()
	const { hasAnyPermission } = reactor(usePermissionStore())
	const thColumnClass = '!bg-gray-100  text-xs !text-center !text-gray-600'
	const TableColumns = computed<TableColumnProps[]>(() => {
		let column = [
			{
				dataIndex: 'details',
				key: 'details',
				title: 'Details',
				customHeaderCell: () => ({
					class: `!rounded-tr-lg ${thColumnClass}`
				}),
				customCell: () => ({
					class: 'md:!text-center text-xs'
				}),
				width: 100
			}
		]
		if (String(props.status).includes('queued')) {
			column = []
		}
		return [
			{
				dataIndex: 'sn',
				key: 'sn',
				title: 'S/N',
				align: 'center',
				width: 50,
				customHeaderCell: () => ({
					class: `!rounded-tl-lg ${thColumnClass}`
				}),
				customCell: () => ({
					class: 'text-xs'
				})
			},
			{
				dataIndex: 'name',
				key: 'name',
				title: 'Name',
				width: 100,
				customHeaderCell: () => ({
					class: thColumnClass
				}),
				customCell: () => ({
					class: ' text-xs'
				})
			},
			{
				dataIndex: 'policy_number',
				key: 'policy_number',
				title: 'Policy ID',
				width: 100,
				customHeaderCell: () => ({
					class: thColumnClass
				}),
				customCell: () => ({
					class: 'md:!text-center text-xs'
				})
			},
			{
				dataIndex: 'status',
				key: 'status',
				title: 'Status',
				width: 100,
				customHeaderCell: () => ({
					class: thColumnClass
				})
			},
			...column
		]
	})
	const { employeePlans, getInactivePendingPolicies } = useEmployeesApi()

	defineEmits<{
		(event: 'view', param: unknown): void
	}>()
	const tableOptions = computed<TableFilterOptions>(() => ({
		cache: 0,
		watch: [props.status]
	}))
	const fetchEmployeePolicies = async () => {
		if (props.status === 'queued') {
			const result = await getInactivePendingPolicies(String(props.employeeId))
			return result
		}
		return await employeePlans(String(props.employeeId), {
			is_active: props.status === 'active' || !props.status,
			is_deactivated: props.status === 'deactivated'
		})
	}
</script>

<template>
	<ui-data-table
		:loading="loading"
		:records="fetchEmployeePolicies"
		:columns="TableColumns"
		:options="tableOptions"
		:filter="filter"
		class="mt-1"
	>
		<template #bodyCell="{ column, record }">
			<div
				v-if="column?.key === 'policy_number'"
				class="max-w-[60px] overflow-hidden text-ellipsis whitespace-nowrap"
			>
				<a-tooltip color="white">
					{{ record?.policy_number || 'N/A' }}
					<template v-if="record?.policy_number" #title>
						<span class="text-gray-500">
							{{ record.policy_number }}
						</span>
					</template>
				</a-tooltip>
			</div>
			<span v-if="column?.key === 'name'" class="capitalize">
				{{ record?.purchase?.product?.name ?? record?.product?.name ?? 'N/A' }}
			</span>
			<template v-if="column?.key === 'status'">
				<my-cover-plan-status v-if="status == 'active'" :plan-details="record" />
				<shared-badge
					v-else
					:text="record?.is_pending ? 'pending' : record?.is_deactivated ? 'deactivated' : 'queued'"
					:pending="record?.is_pending || !!record?.product?.name"
					:failed="record?.is_deactivated"
					class="!text-xs"
				/>
			</template>

			<template v-if="column?.key === 'details'">
				<button
					v-if="
						hasAnyPermission([PERMISSIONS.PLAN.VIEW_DETAILS]) &&
						(record?.is_pending || record?.is_deactivated || status == 'active')
					"
					type="button"
					class="px-4 py-3.5 font-medium text-primary-2 hover:text-primary-300"
					@click="$emit('view', record)"
				>
					View
				</button>
				<span v-else-if="!hasAnyPermission([PERMISSIONS.PLAN.VIEW_DETAILS])" class="italic text-gray-400">
					permission required
				</span>
				<span v-else>awaiting</span>
			</template>
		</template>
	</ui-data-table>
</template>
